import axios from 'axios';
import { api as service } from 'utils/axios';
import { getAuth, sendPasswordResetEmail, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { auth } from './firebase';

// Configurar la persistencia de Firebase
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting persistence:', error);
}
);

const instance = axios.create({
  baseURL: process.env.REACT_APP_API,
});

let isRefreshing = false;
let failedQueue = [];
let retryCount = 0;
const MAX_RETRIES = 3;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const resetRetryCount = () => {
  setTimeout(() => {
    retryCount = 0;
  }, 60000); // Reset retry count after 1 minute
};

instance.interceptors.request.use(async (config) => {
  const user = auth.currentUser;

  if (user) {
    try {
      const token = await user.getIdToken(false);
      config.headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('Error getting token:', error);
      // No lanzar error aquí, permitir que la petición continúe
    }
  }

  return config;
});

instance.interceptors.response.use(
  (response) => {
    retryCount = 0; // Reset on successful response
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const { response } = error;


    if (response?.status === 403 || response?.status === 401) {
      const errorCode = response?.data?.code;


      if (errorCode === 'firebase-expired-token' || errorCode === 'firebase-invalid-token') {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {

              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          const user = auth.currentUser;
          if (user) {
            console.log('🔄 Intentando renovar token:', {
              timestamp: new Date().toISOString(),
              userId: user.uid,
              lastTokenRefresh: user.metadata.lastSignInTime,
            });

            retryCount++;


            if (retryCount >= MAX_RETRIES) {
              console.error('Max retries reached, forcing sign out', {
                retryCount,
                errorCode,
                lastEndpoint: originalRequest?.url,
              });
              await signOut(auth);
              window.location.replace('/sign-in');
              return Promise.reject(new Error('Max retries reached'));
            }

            console.log('Attempting token refresh', { retryCount });
            const newToken = await user.getIdToken(true);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            instance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            processQueue(null, newToken);
            resetRetryCount();
            console.log('✅ Token renovado exitosamente');
            return instance(originalRequest);
          } else {
            console.log('❌ No hay usuario autenticado');
          }
        } catch (refreshError) {
          console.error('❌ Error al renovar token:', {
            timestamp: new Date().toISOString(),
            code: refreshError.code,
            message: refreshError.message,
            requiresLogout: !['auth/network-request-failed', 'auth/timeout'].includes(refreshError.code),
          });

          if (!['auth/network-request-failed', 'auth/timeout'].includes(refreshError.code)) {
            await signOut(auth);
            window.location.replace('/sign-in');
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }
    return Promise.reject(error);
  }
);

export const upload = axios.create({
  baseURL: process.env.REACT_APP_UPLOAD_API,
});

upload.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export default instance;
export const apiUrl = process.env.REACT_APP_API;

export const resetPassword = (email, callback) => {
  const auth = getAuth();
  sendPasswordResetEmail(auth, email)
    .then(() => {
      // Correo de restablecimiento de contraseña enviado exitosamente.
      console.log('Correo de restablecimiento de contraseña enviado exitosamente');
      callback();
    })
    .catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message;
      // Manejar errores
      console.error('Error al enviar el correo de restablecimiento de contraseña:', errorCode, errorMessage);
    });
};
export const upgradePlan = async (name, country) => {
  let url = process.env.REACT_APP_SHEETY_API;
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contact: {
        name: name,
        country: country,
      },
    }),
  }).then((response) => {
    return response.json();
  });
};

export const createLogoutRecord = async () => {
  try {
    const sessionId = localStorage.getItem('chestmusic_session_id');
    const response = await service.post('session/logout/', {
      id: sessionId,
    });
    if (response.status === 204) {
      localStorage.removeItem('chestmusic_session_id');
    }
  } catch (error) {
    console.error('Error creating logout record:', error);
  }
};

export const createLoginRecord = async (deviceData) => {
  try {
    const response = await service.post('session/login/', deviceData);
    if (response.status === 201) {
      localStorage.setItem('chestmusic_session_id', response.data.id);
    }
  } catch (error) {
    console.error('Error creating login record:', error);
  }
};
